import {
  exportCsvConverter,
  pagableDataConverter,
} from 'data/convertors/general.converters';
import {
  TExportCsvDB,
  TExportCsvUI,
  TPageableDataWithContentDB,
  TPageableDataWithContentUI,
  TTableFilters,
} from 'data/types/generic.types';
import { authSplitApi } from 'redux/helpers/slice.helpers';
import {
  EEventParentsUrls,
  getEventParentsUrl,
} from 'data/urls/eventParents.url';
import {
  EEventParentsFilterParams,
  TEditEventParentUI,
  TEventParentDataDB,
  TEventParentDataUI,
  TEventParentsExportRequest,
} from 'data/types/eventParents.types';
import {
  getEventParentConvertor,
  eventParentsFilterConverter,
  eventParentUpdateConverter,
} from 'data/convertors/eventParents.convertor';
import { downloadFile } from 'redux/services/fileServices';

export const eventParentsApi = authSplitApi('eventParents', [
  'event-parents',
]).injectEndpoints({
  endpoints: build => ({
    getEventParentsList: build.query<
      TPageableDataWithContentUI<TEventParentDataUI[]>,
      TTableFilters<EEventParentsFilterParams> & { isActiveOnly?: true }
    >({
      query({
        page,
        pageSize,
        filters,
        sortColumn: { sortDirection, sortField },
        isActiveOnly,
      }) {
        return {
          url: getEventParentsUrl,
          method: 'GET',
          params: {
            page,
            size: pageSize,
            ...(sortField && sortDirection && { sortField, sortDirection }),
            ...(filters && {
              ...eventParentsFilterConverter(filters),
            }),
            ...(isActiveOnly && { isActiveOnly }),
          },
        };
      },
      providesTags: ['event-parents'],
      transformResponse: (
        data: TPageableDataWithContentDB<TEventParentDataDB[]>,
      ) => {
        return {
          ...pagableDataConverter.fromDb(data),
          content: (data.content || []).map(parent =>
            getEventParentConvertor.fromDb(parent),
          ),
        };
      },
    }),
    exportEventParentsCSV: build.mutation<
      TExportCsvUI,
      TEventParentsExportRequest
    >({
      query(body) {
        return {
          url: `${getEventParentsUrl}/${EEventParentsUrls.EXPORT}`,
          method: 'POST',
          body,
        };
      },
      transformResponse: (data: TExportCsvDB) => {
        const convertedData = exportCsvConverter('text/csv').fromDb(data);
        downloadFile(convertedData.content, convertedData.fileName);
        return convertedData;
      },
    }),
    editEventParent: build.mutation<void, TEditEventParentUI>({
      query(body) {
        return {
          url: getEventParentsUrl,
          method: 'PATCH',
          body: eventParentUpdateConverter.toDb(body),
        };
      },
      invalidatesTags: ['event-parents'],
    }),
  }),
});

export const {
  useGetEventParentsListQuery,
  useEditEventParentMutation,
  useExportEventParentsCSVMutation,
} = eventParentsApi;
